import { useState } from 'react';
import { typography, semantic, shadow, radius } from '@/shared/theme/tokens';
import { useShifts, useCreateShift, useUpdateShift, useDeleteShift } from '@/features/shifts/hooks/useShifts';
import { useProductionDay, useSetProductionDay, useClearProductionDay } from '@/features/productionDay/hooks/useProductionDay';
import { useApiErrorToast, useToast } from '@/shared/ui/Toast';
import { SkeletonCard } from '@/shared/ui/SkeletonCard';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { ModalShell } from '@/shared/ui/ModalShell';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { OverviewConfigSection } from '@/features/overviewDiagram/ui/OverviewConfigSection';
import { AppTimePicker } from '@/shared/ui/DateTimeField';
import { timeToDayjs, dayjsToTime } from '@/shared/lib/dateHelpers';

// Cross-midnight: end < start means next day (e.g. 22:00–06:00)
function shiftDurationLabel(start, end) {
  if (!start || !end) return '';
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins <= 0) mins += 24 * 60;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function crossesMidnight(start, end) {
  if (!start || !end) return false;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  return (eh * 60 + em) <= (sh * 60 + sm);
}

const EMPTY_FORM = { name: '', startTime: '', endTime: '' };

function validate(form) {
  if (!form.name.trim()) return 'Shift name is required.';
  if (!form.startTime) return 'Start time is required.';
  if (!form.endTime) return 'End time is required.';
  if (form.startTime === form.endTime) return 'Start and end time cannot be the same.';
  return null;
}

// ── Shift Form Modal ──────────────────────────────────────────────────────────
function ShiftFormModal({ mode, initial, onSubmit, onClose, pending, c, isDark }) {
  const [form, setForm] = useState(initial ?? EMPTY_FORM);
  const [error, setError] = useState('');

  function setField(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  function handleSubmit(e) {
    e.preventDefault();
    const err = validate(form);
    if (err) { setError(err); return; }
    onSubmit(form);
  }

  const inputStyle = {
    width: '100%', padding: '8px 10px', borderRadius: radius.md, fontSize: typography.fontSize.md,
    border: `1px solid ${c.border}`, background: c.bgSubtle, color: c.textPrimary,
    fontFamily: typography.fontFamily.body, outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: c.textSubtle, marginBottom: 4, display: 'block' };

  const midnight = crossesMidnight(form.startTime, form.endTime);

  return (
    <ModalShell title={mode === 'create' ? 'Add Shift' : 'Edit Shift'} onClose={onClose} c={c} isDark={isDark}>
      <form id="shift-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={labelStyle}>Shift Name</label>
          <input
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            placeholder="e.g. A, B, C, A1, Day, Night"
            style={inputStyle}
            autoFocus
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={labelStyle}>Start Time</label>
            <AppTimePicker value={timeToDayjs(form.startTime)} onChange={(d) => setField('startTime', dayjsToTime(d))} />
          </div>
          <div>
            <label style={labelStyle}>End Time</label>
            <AppTimePicker value={timeToDayjs(form.endTime)} onChange={(d) => setField('endTime', dayjsToTime(d))} />
          </div>
        </div>
        {midnight && (
          <div style={{ fontSize: typography.fontSize.sm, color: semantic.infoCyan, display: 'flex', alignItems: 'center', gap: 5 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 13 }}>info</span>
            End time is next day (cross-midnight shift) — duration: {shiftDurationLabel(form.startTime, form.endTime)}
          </div>
        )}
        {!midnight && form.startTime && form.endTime && (
          <div style={{ fontSize: typography.fontSize.sm, color: semantic.successGreen, display: 'flex', alignItems: 'center', gap: 5 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 13 }}>schedule</span>
            Duration: {shiftDurationLabel(form.startTime, form.endTime)}
          </div>
        )}
        {error && <div style={{ fontSize: typography.fontSize.sm, color: semantic.error }}>{error}</div>}
      </form>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
        <PrimaryButton variant="secondary" onClick={onClose}>Cancel</PrimaryButton>
        <PrimaryButton type="submit" form="shift-form" disabled={pending} icon={mode === 'create' ? 'add' : 'check'}>
          {mode === 'create' ? 'Add Shift' : 'Save'}
        </PrimaryButton>
      </div>
    </ModalShell>
  );
}

// ── Shift Chip (card) ────────────────────────────────────────────────────────
function ShiftChip({ shift, onEdit, onDelete, canManage, c }) {
  const [hovered, setHovered] = useState(false);
  const midnight = crossesMidnight(shift.startTime, shift.endTime);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
        borderRadius: radius.xl, border: `1px solid ${hovered ? 'rgba(62,181,231,0.25)' : c.border}`,
        background: hovered ? c.bgSubtle : c.bg,
        transition: 'background 0.12s, border-color 0.12s',
        minWidth: 0,
      }}
    >
      <div style={{
        width: 42, height: 42, borderRadius: radius.lg, flexShrink: 0,
        background: semantic.brandBg, border: `1px solid ${semantic.brandBorderSoft}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.extrabold, color: semantic.brandNavyMid, letterSpacing: typography.letterSpacing.tight,
      }}>
        {shift.name}
      </div>
      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <div style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: c.textPrimary, marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          Shift {shift.name}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
          <span className="material-symbols-outlined" style={{ fontSize: 12, color: c.textSubtle, flexShrink: 0 }}>schedule</span>
          <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {shift.startTime} – {shift.endTime}
          </span>
          {midnight && (
            <span style={{ fontSize: typography.fontSize.xs, color: semantic.infoCyan, fontWeight: typography.fontWeight.bold, background: semantic.infoCyanBg, border: `1px solid ${semantic.infoCyanBorderMid}`, borderRadius: radius.xs, padding: '1px 4px', whiteSpace: 'nowrap', flexShrink: 0 }}>
              +1d
            </span>
          )}
          <span style={{ fontSize: typography.fontSize.sm, color: c.border, flexShrink: 0 }}>·</span>
          <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {shiftDurationLabel(shift.startTime, shift.endTime)}
          </span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0, opacity: canManage && hovered ? 1 : 0, pointerEvents: canManage && hovered ? 'auto' : 'none', transition: 'opacity 0.12s' }}>
        <button
          onClick={() => onEdit(shift)}
          style={{ background: 'none', border: `1px solid ${c.border}`, borderRadius: radius.md, padding: '5px 9px', cursor: 'pointer', color: c.textSubtle, display: 'flex', alignItems: 'center' }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>edit</span>
        </button>
        <button
          onClick={() => onDelete(shift)}
          style={{ background: 'none', border: `1px solid ${semantic.errorBorderSoft}`, borderRadius: radius.md, padding: '5px 9px', cursor: 'pointer', color: semantic.error, display: 'flex', alignItems: 'center' }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>delete</span>
        </button>
      </div>
    </div>
  );
}

// ── OverviewConfig (embedded in Settings tab) ─────────────────────────────────

export function ShiftsTab({ deploymentId, canManage }) {
  const c = useThemeColors();
  const isDark = useIsDark();
  const toast = useToast();
  const toastApiError = useApiErrorToast();

  const { data: shifts = [], isLoading } = useShifts(deploymentId);
  const createShift = useCreateShift(deploymentId);
  const updateShift = useUpdateShift(deploymentId);
  const deleteShift = useDeleteShift(deploymentId);

  const [modal, setModal] = useState(null); // null | { mode: 'create' } | { mode: 'edit', shift }
  const [deleteTarget, setDeleteTarget] = useState(null);

  async function handleCreate(form) {
    setModal(null);
    try {
      await createShift.mutateAsync(form);
      toast({ message: 'Shift added', type: 'success' });
    } catch (err) { toastApiError(err); }
  }

  async function handleUpdate(form) {
    const shiftId = modal.shift.id;
    setModal(null);
    try {
      await updateShift.mutateAsync({ shiftId, payload: form });
      toast({ message: 'Shift updated', type: 'success' });
    } catch (err) { toastApiError(err); }
  }

  async function handleDelete() {
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      await deleteShift.mutateAsync(target.id);
      toast({ message: 'Shift deleted', type: 'success' });
    } catch (err) { toastApiError(err); }
  }

  if (isLoading) return <SkeletonCard rows={4} height={200} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── Row: Shift Master (wide) + Production Day (compact) ── */}
      <div style={{ display: 'flex', gap: 20, alignItems: 'stretch', flexWrap: 'wrap' }}>
      <div style={{ flex: '2 1 420px', minWidth: 0, display: 'flex' }}>
      <ShiftsIsland label="Shift Master" icon="schedule" c={c} isDark={isDark}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span style={{ fontSize: typography.fontSize.md, color: c.textSubtle }}>
              Configure shift names and timings. Cross-midnight shifts are supported.
            </span>
            {canManage && (
              <PrimaryButton icon="add" onClick={() => setModal({ mode: 'create' })}>Add Shift</PrimaryButton>
            )}
          </div>

          {shifts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '36px 0', color: c.textSubtle, fontSize: typography.fontSize.md, border: `1px dashed ${c.borderSubtle}`, borderRadius: radius.lg }}>
              <span className="material-symbols-outlined" style={{ fontSize: 32, display: 'block', marginBottom: 8, opacity: 0.4 }}>schedule</span>
              No shifts configured yet.{canManage ? ' Click "Add Shift" to get started.' : ''}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
              {shifts.map((shift) => (
                <ShiftChip
                  key={shift.id}
                  shift={shift}
                  onEdit={(s) => setModal({ mode: 'edit', shift: s })}
                  onDelete={setDeleteTarget}
                  canManage={canManage}
                  c={c}
                />
              ))}
            </div>
          )}
        </div>
      </ShiftsIsland>
      </div>

      {/* Production Day — compact, beside Shift Master */}
      <div style={{ flex: '1 1 280px', minWidth: 0, display: 'flex' }}>
        <ProductionDaySection deploymentId={deploymentId} canManage={canManage} c={c} isDark={isDark} />
      </div>
      </div>

      {/* Add/Edit modal */}
      {modal && (
        <ShiftFormModal
          mode={modal.mode}
          initial={modal.mode === 'edit' ? { name: modal.shift.name, startTime: modal.shift.startTime, endTime: modal.shift.endTime } : EMPTY_FORM}
          onSubmit={modal.mode === 'create' ? handleCreate : handleUpdate}
          onClose={() => setModal(null)}
          pending={createShift.isPending || updateShift.isPending}
          c={c}
          isDark={isDark}
        />
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <ModalShell title="Delete Shift" onClose={() => setDeleteTarget(null)} c={c} isDark={isDark}>
          <p style={{ fontSize: typography.fontSize.md, color: c.textPrimary, margin: '0 0 20px' }}>
            Delete <strong>Shift {deleteTarget.name}</strong> ({deleteTarget.startTime} – {deleteTarget.endTime})? This cannot be undone.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <PrimaryButton variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</PrimaryButton>
            <PrimaryButton variant="danger" onClick={handleDelete} disabled={deleteShift.isPending} icon="delete">Delete</PrimaryButton>
          </div>
        </ModalShell>
      )}

      {/* ── Island: Overview Configuration ── */}
      <ShiftsIsland label="Overview Configuration" icon="home_app_logo" c={c} isDark={isDark}>
        <OverviewConfigSection deploymentId={deploymentId} canManage={canManage} />
      </ShiftsIsland>

    </div>
  );
}

// ── Production Day ─────────────────────────────────────────────────────────────
// Single "Day starts at" time picker. The production day is a 24h cycle, so the
// API stores startTime === endTime; the form submits one time for both.
function ProductionDaySection({ deploymentId, canManage, c, isDark }) {
  const toast = useToast();
  const toastApiError = useApiErrorToast();
  const { data: config, isLoading } = useProductionDay(deploymentId);
  const setDay = useSetProductionDay(deploymentId);
  const clearDay = useClearProductionDay(deploymentId);

  const [draft, setDraft] = useState(null); // HH:MM | null (null = follow fetched value)
  // The effective shown value: local edit if present, else the fetched start.
  const shown = draft ?? config?.startTime ?? '';
  const isOverride = config?.source === 'override';
  const dirty = draft != null && draft !== config?.startTime;
  const busy = setDay.isPending || clearDay.isPending;

  async function handleSave() {
    if (!shown) return;
    try {
      await setDay.mutateAsync({ startTime: shown, endTime: shown });
      setDraft(null);
      toast({ message: 'Production day updated', type: 'success' });
    } catch (err) { toastApiError(err); }
  }

  async function handleReset() {
    try {
      await clearDay.mutateAsync();
      setDraft(null);
      toast({ message: 'Reverted to default', type: 'success' });
    } catch (err) { toastApiError(err); }
  }

  return (
    <ShiftsIsland label="Production Day" icon="event_repeat" c={c} isDark={isDark}>
      {isLoading ? (
        <SkeletonCard rows={2} height={120} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, height: '100%' }}>
          <span style={{ fontSize: typography.fontSize.md, color: c.textSubtle }}>
            The 24-hour cycle day-wise stats are grouped by (IST).
          </span>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold, letterSpacing: typography.letterSpacing.wider, textTransform: 'uppercase', color: c.textMuted, display: 'flex', alignItems: 'center', gap: 6 }}>
              Day starts at
              {!isOverride && (
                <span style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, color: c.textSubtle, background: isDark ? 'rgba(255,255,255,0.06)' : c.bgMuted, padding: '1px 6px', borderRadius: radius.xs, textTransform: 'none', letterSpacing: 0 }}>
                  default
                </span>
              )}
            </label>
            <AppTimePicker
              value={timeToDayjs(shown)}
              onChange={(d) => setDraft(dayjsToTime(d))}
              disabled={!canManage || busy}
              slotProps={{ textField: { inputProps: { 'aria-label': 'day-starts-at' } } }}
            />
          </div>

          {canManage && (
            <div style={{ display: 'flex', gap: 8, marginTop: 'auto', justifyContent: 'flex-end' }}>
              <PrimaryButton variant="secondary" size="sm" onClick={handleReset} disabled={!isOverride || busy}>
                Reset to default
              </PrimaryButton>
              <PrimaryButton icon="check" size="sm" onClick={handleSave} disabled={!dirty || busy}>
                Save
              </PrimaryButton>
            </div>
          )}
        </div>
      )}
    </ShiftsIsland>
  );
}

function ShiftsIsland({ label, icon, c, isDark, children }) {
  return (
    <div style={{
      background: isDark ? 'rgba(255,255,255,0.025)' : '#fff',
      border: `1px solid ${c.border}`,
      borderRadius: radius['2xl'],
      boxShadow: shadow.panel,
      overflow: 'hidden',
      width: '100%', height: '100%',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 9,
        padding: '13px 20px',
        borderBottom: `1px solid ${c.border}`,
        background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(248,250,252,1)',
        flexShrink: 0,
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 16, color: c.textSubtle }}>{icon}</span>
        <span style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.bold, color: c.textPrimary, fontFamily: typography.fontFamily.body, letterSpacing: typography.letterSpacing.normal }}>
          {label}
        </span>
      </div>
      <div style={{ padding: '20px', flex: 1 }}>
        {children}
      </div>
    </div>
  );
}


